import { LotameClient } from "../../lotame/LotameClient";
import { sendMediaMetadata } from "./handlers/SendMediaMetadata";
import { inject, injectable, singleton } from "tsyringe";
import { sendBehaviorsOnLoad } from "./handlers/SendBehaviorsOnLoad";
import { sendSubscriptionCompleted } from "./handlers/SendSubscriptionCompleted";
import { Logger } from "../../utils/Logger";
import { sendSponsor } from "./handlers/SendSponsor";
import { ArraysAreEquivalents } from "../../shared/ArrayUtils";
import { setGPTTargeting } from "../../googleTagPublisher/gtpUtils";
import { SiteRules } from "../../shared/SiteRules";
import { GeoIpService } from "../../shared/geoLocation/GeoIpService";

@injectable()
@singleton()
export class LotameService {
  constructor(
    @inject(LotameClient) private lotameClient: LotameClient,
    @inject(SiteRules) private siteRules: SiteRules,
    @inject(GeoIpService) private geoIpService: GeoIpService,
  ) {
    this.sendAvailableAudiences();
  }

  private previousAudiences: Array<string> = [];

  // noinspection JSUnusedGlobalSymbols
  /**
   * Called after 'aaTrackPageView' in the 'lotame_preconnect' tag.
   * Must be called before other methods.
   * @param planName
   * The subscription plan name of the current user.
   * @param discountName
   * The subscription discount name of the current user.
   */
  public initialize(planName: string, discountName: string): void {
    try {
      // Configure tasks to run on each profile load.
      if (!this.siteRules.isTrackingEnabled) {
        Logger.verbose("Tracking disabled: aborting invokeWhenLotameProfileUpdated");
      } else {
        this.lotameClient.invokeWhenLotameProfileUpdated(async () => {
          const postalCode = await this.geoIpService.fetchAndStoreUserPostalCode();
          sendBehaviorsOnLoad(planName, discountName, this.lotameClient, postalCode);
          this.sendAudiencesToGAM();
        });
      }
      // Call to create load Lotame's script and
      // create the window.lotame_<clientId> variable in the page
      this.lotameClient.lotameLightningTagSetup();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      Logger.warn(`Erreur dans l'envoi au DMP: ${err}. Trace: ${err.stack}`);
    }
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Handle the MediaMetaDataLoaded event.
   * Called in the GTM tag 'lotame_sendMediaMetadataLoaded'
   * @param planName
   * The subscription plan name of the current user.
   * @param discountName
   * The subscription discount name of the current user.
   */
  public handleMediaMetaDataLoaded(planName: string, discountName: string): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleMediaMetaDataLoaded");
      return;
    }
    this.lotameClient.invokeWhenLotameProfileReady(() =>
      sendMediaMetadata("mLoad", planName, discountName, this.lotameClient),
    );
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Handle the ContentStart event (when a media is started, after the ads)
   * Called in the GTM tag 'lotame_sendContentStart'
   * @param planName
   The subscription plan name of the current user.
   * @param discountName
   The subscription discount name of the current user.
   */
  public handleContentStart(planName: string, discountName: string): void {
    this.lotameClient.invokeWhenLotameProfileReady(() => {
      sendMediaMetadata("Stream", planName, discountName, this.lotameClient);
      sendSponsor(this.lotameClient);
    });
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Send a behavior when a user subscription is completed.
   * Called in the GTM tag 'lotame_sendSubscriptionComplete'
   * @param planName
   * The subscription plan name of the current user.
   * @param discountName
   * The subscription discount name of the current user.
   */
  public sendSubscriptionComplete(planName: string, discountName: string): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting sendSubscriptionCompleted");
      return;
    }
    this.lotameClient.invokeWhenLotameProfileReady(() => {
      sendSubscriptionCompleted(planName, discountName, this.lotameClient);
    });
  }

  public handlePageView(): void {
    this.lotameClient.triggerPageView();
  }

  private sendAudiencesToGAM(): void {
    const audiences = this.lotameClient.getAudiences();
    if (ArraysAreEquivalents(audiences, this.previousAudiences)) {
      Logger.verbose("Unchanged audiences");
      return;
    }

    if (audiences?.length > 0) {
      Logger.verbose(`Sending ${audiences.length} audiences to GAM`);
      this.sendAudienceList(audiences);
      this.previousAudiences = audiences;
    }
  }

  private sendAvailableAudiences(): void {
    const localStorageAudiences = this.lotameClient.getAudiencesFromLocalStorage();
    if (localStorageAudiences.length > 0) {
      this.sendAudienceList(localStorageAudiences);
      // Warning: this message is used in Playwright tests.
      Logger.verbose(`Lotame Page load: Sent available audiences (${localStorageAudiences.length}) to GAM`);
      this.previousAudiences = localStorageAudiences;
    } else {
      Logger.verbose("No audience available in local storage");
    }
  }

  private sendAudienceList(audiences: string[]): void {
    setGPTTargeting("audienceList", audiences);
  }
}
